/* eslint-disable max-len */
import React, {type RefObject} from 'react';

interface CheckProps {
    /**
     * A string representing the CSS class to be applied to the CheckIcon element to override its default styling.
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     */
    color1?: string;
    /**
     * A number or string representing the height of the CheckIcon element.
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     */
    testId?: string;
    /**
     * A number or string representing the height of the CheckIcon element.
     */
    width?: number | string;
}

/**
 * The `Check` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `CheckProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the CheckIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the CheckIcon element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the CheckIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const CheckComponent = <Check className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Check = ({
    className = '',
    color1 = '#82C452',
    height = 20,
    ref,
    testId = 'Check',
    width = 20
}: CheckProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 20 20"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path clipRule="evenodd" d="M17.4 4.26c.41.41.41 1.07 0 1.48L8.24 14.9c-.41.41-1.07.41-1.48 0L2.6 10.74a1.04 1.04 0 1 1 1.47-1.48L7.5 12.7l8.43-8.43c.4-.4 1.07-.4 1.47 0" fill={color1} fillRule="evenodd" />
    </svg>
);

Check.displayName = 'Check';